/**
 * TimelineSceneColours — the scene-colourising concern of the home timeline:
 * the spaces / systems tint passes (the HomeWebGLDeps.colourise* entry
 * points, called from the swap side with DepartmentColours colour sets) and
 * the lazy sprite-ref gathering that exists ONLY to be colourised — the
 * refs, the gathering and the colourising move together.
 *
 * HomeTimeline keeps 2-line public facades (the HomeWebGLDeps signatures are
 * unchanged); bodies are a verbatim extraction from HomeTimeline. The item
 * registry is sampled via deps (storage stays timeline-side); the build-once
 * ref guards are preserved exactly. No HomeColours import — the colour sets
 * arrive as arguments from the swap side.
 */
import type { TimelineSceneColoursDeps } from 'app/views/home/timeline/timeline.types';
import { TimelineItem } from 'app/views/home/timeline/TimelineItem';

export class TimelineSceneColours {
	private _deps: TimelineSceneColoursDeps;

	private _spacesItemRefs: Array<TimelineItem> = null;
	private _screenBgItem: TimelineItem = null;
	private _screenFloorItem: TimelineItem = null;
	private _screenBgParticleItem: TimelineItem = null;
	private _screenBgParticleItem2: TimelineItem = null;
	private _screenBgParticleItem3: TimelineItem = null;

	private _systemsItemRefs: Array<TimelineItem> = null;

	private _spacesBene;
	private _spacesBene2;
	private _spacesAmy;
	private _spacesAmy2;

	constructor(deps: TimelineSceneColoursDeps) {
		this._deps = deps;
	}

	//The single-ref members can be undefined when their item is missing from
	//the registry (webglTypes-filtered — see TimelineItemFactory; the empty
	//lookup warns on the dev harness) — every tint below dereferences them
	//with `?.`, and the list builders push only found items:
	private _createsSpacesItemRefs() {
		this._spacesItemRefs = [];

		this._screenBgItem = this._deps.getItems('box')[0];
		this._screenFloorItem = this._deps.getItems('floorhighligt')[0];

		this._spacesBene = this._deps.getItems('person')[0];
		this._spacesBene2 = this._deps.getItems('personbase')[0];

		this._spacesAmy = this._deps.getItems('amy-base')[0];
		this._spacesAmy2 = this._deps.getItems('amy-highlight')[0];

		this._screenBgParticleItem = this._deps.getItems('largescreenp')[0];
		this._screenBgParticleItem2 = this._deps.getItems('largescreenp2')[0];
		this._screenBgParticleItem3 = this._deps.getItems('largescreenp3')[0];

		let spacebgarea = this._deps.getItems('spacebgarea')[0];
		if (spacebgarea) {
			this._spacesItemRefs.push(spacebgarea);
		}

		let spacesp = this._deps.getItems('spacesp');
		spacesp.forEach((p) => {
			this._spacesItemRefs.push(p);
		});
	}

	private _createsSystemsItemRefs() {
		this._systemsItemRefs = [];

		['systemsp1', 'systemsp2', 'systemsp4', 'systemsp5', 'systemsp6'].forEach((id) => {
			let item = this._deps.getItems(id)[0];
			if (item) {
				this._systemsItemRefs.push(item);
			}
		});
	}

	public colouriseSystemsScene(colourSet, instant) {
		if (!this._systemsItemRefs) {
			this._createsSystemsItemRefs();
		}

		let delay = instant ? 0 : 1000;

		this._systemsItemRefs.forEach((item) => {
			let colour = colourSet[Math.floor(Math.random() * (colourSet.length - 0.01))];

			setTimeout(() => {
				item.setTint(colour);
			}, delay);

			delay += instant ? 0 : 700;
		});
	}

	public colouriseSpacesScene(colourSet, screenColour: number | string = null, floorColour = null) {
		if (!this._spacesItemRefs) {
			this._createsSpacesItemRefs();
		}

		this._spacesItemRefs.forEach((item) => {
			let colour = colourSet[Math.floor(Math.random() * (colourSet.length - 0.01))];
			item.setTint(colour);
		});

		let screenC = screenColour;
		let floorC = floorColour;

		if (screenColour === null) {
			screenC = colourSet[Math.floor(Math.random() * (colourSet.length - 0.01))];
		}
		this._screenBgItem?.setTint(screenC);

		if (floorColour === null) {
			floorC = colourSet[Math.floor(Math.random() * (colourSet.length - 0.01))];
		}

		this._screenFloorItem?.setTint(floorC);
		this._screenBgParticleItem?.setTint(floorC);
		this._screenBgParticleItem2?.setTint(floorC);
		this._screenBgParticleItem3?.setTint(floorC);

		this._spacesBene?.setTint(floorC);
		this._spacesBene2?.setTint(floorC);

		this._spacesAmy?.setTint(floorC);
		this._spacesAmy2?.setTint(floorC);
	}
}
